import { ref, computed, onMounted, onUnmounted } from "vue";

export interface CountdownComposable {
  timeRemaining: Ref<string>;
  isExpired: Ref<boolean>;
  secondsRemaining: Ref<number>;
  minutesRemaining: Ref<number>;
  isUrgent: Ref<boolean>;
  expiresOnRef: Ref<Date | undefined | null>;
  reset: (expiresOnUpdated?: Date) => void;
}

/**
 * Composable for creating countdown timers
 * @param expiresOn - Date when the timer expires
 * @returns Object containing timeRemaining string, isExpired flag, and secondsRemaining
 */
export const useCountdown = (expiresOn?: Date): CountdownComposable => {
  const expiresOnRef = ref(expiresOn);
  const timeRemaining = ref<string>("");
  const secondsRemaining = ref<number>(0);
  const minutesRemaining = ref<number>(0);
  const isExpired = ref<boolean>(false);
  const isUrgent = ref<boolean>(false);
  let intervalId: NodeJS.Timeout | null = null;

  const reset = (expiresOnUpdated?: Date) => {
    if (expiresOnUpdated) expiresOnRef.value = expiresOnUpdated;
    calculateTimeRemaining();
    if (!isExpired.value && expiresOnRef.value) {
      intervalId = setInterval(calculateTimeRemaining, 1000);
    }
  };

  const calculateTimeRemaining = () => {
    if (!expiresOnRef.value) {
      timeRemaining.value = "";
      isExpired.value = true;
      secondsRemaining.value = 0;
      minutesRemaining.value = 0;
      isUrgent.value = false;
      return;
    }

    const now = new Date();
    const expireDate = new Date(expiresOnRef.value);
    const diff = expireDate.getTime() - now.getTime();

    if (diff <= 0) {
      timeRemaining.value = "Expired";
      isExpired.value = true;
      secondsRemaining.value = 0;
      minutesRemaining.value = 0;
      isUrgent.value = false;
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      return;
    }

    secondsRemaining.value = Math.floor(diff / 1000);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    // Calculate total minutes remaining
    minutesRemaining.value = Math.floor(diff / (1000 * 60));

    // Mark as urgent if less than 5 minutes remaining
    isUrgent.value = minutesRemaining.value < 5;

    if (hours > 0) {
      timeRemaining.value = `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      timeRemaining.value = `${minutes}m ${seconds}s`;
    } else {
      timeRemaining.value = `${seconds}s`;
    }

    isExpired.value = false;
  };

  onMounted(() => {
    reset();
  });

  onUnmounted(() => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  });

  return {
    timeRemaining,
    isExpired,
    secondsRemaining,
    minutesRemaining,
    isUrgent,
    expiresOnRef,
    reset,
  };
};
